import { useRef } from "react";
import { ArrowLeft, Check, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ProhibitedKeywordAddFormProps {
  single: string;
  onSingleChange: (value: string) => void;
  onAddSingle: () => void;
  bulk: string;
  onBulkChange: (value: string) => void;
  onBulkAdd: () => void;
  onFileUpload: (file: File) => void;
  onChangeScope: () => void;
  isPending: boolean;
}

// AUT-41: the Single/Bulk/File Upload screen of AddProhibitedKeywordDialog, shown once a
// tier has been picked. Purely presentational -- the dialog owns state and submission
// logic, since which mutation fires depends on the selected tier.
export function ProhibitedKeywordAddForm({
  single,
  onSingleChange,
  onAddSingle,
  bulk,
  onBulkChange,
  onBulkAdd,
  onFileUpload,
  onChangeScope,
  isPending,
}: ProhibitedKeywordAddFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onChangeScope}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Change scope
      </button>

      <Tabs defaultValue="single" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="single" className="flex-1">Add Single</TabsTrigger>
          <TabsTrigger value="bulk" className="flex-1">Bulk Add</TabsTrigger>
          <TabsTrigger value="upload" className="flex-1">File Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-4">
          <div className="flex gap-2">
            <Input
              value={single}
              onChange={(e) => onSingleChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddSingle();
                }
              }}
              placeholder="Add a prohibited keyword..."
              className="flex-1"
              autoFocus
              disabled={isPending}
            />
            <Button onClick={onAddSingle} size="sm" disabled={isPending}>
              <Check className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="bulk" className="mt-4 space-y-2">
          <Textarea
            value={bulk}
            onChange={(e) => onBulkChange(e.target.value)}
            placeholder="Paste keywords separated by commas or new lines..."
            rows={5}
            disabled={isPending}
          />
          <Button onClick={onBulkAdd} size="sm" className="w-full" disabled={isPending}>
            <Plus className="h-4 w-4 mr-1" />
            Add All
          </Button>
        </TabsContent>

        <TabsContent value="upload" className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileInputChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending}
            className="w-full flex flex-col items-center gap-2 py-6 rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="h-7 w-7 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              Click to upload <span className="font-medium text-foreground">CSV or TXT</span>
            </div>
            <div className="text-xs text-muted-foreground">One keyword per line, or comma-separated</div>
          </button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
